import { Link } from "wouter";
import { PREBUILT_TRACKERS } from "@macroscope/shared";
import type { CalendarResponse, GeoResponse, PositioningResponse } from "../lib/api";

interface Props {
  calendar?: CalendarResponse;
  geo?: GeoResponse;
  positioning?: PositioningResponse;
}

function trackerHref(scope: string): string {
  const def = PREBUILT_TRACKERS.find((d) => d.scope === scope);
  return `/geopolitics/${def?.id ?? ""}`;
}

export function WhatToWatch({ calendar, geo, positioning }: Props) {
  const now = new Date();
  const nextEvents = (calendar?.events ?? [])
    .filter((e) => new Date(e.datetime_utc) > now && e.impact === "high")
    .slice(0, 5);

  const hotTrackers = (geo?.trackers ?? [])
    .filter((t) => t.value > 50 || t.trend === "rising")
    .slice(0, 3);

  const extremes = (positioning?.positions ?? [])
    .filter((p) => p.percentile > 80 || p.percentile < 20)
    .slice(0, 3);

  return (
    <div className="space-y-4">
      <section>
        <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-400">Next high-impact events</h3>
        {nextEvents.length === 0 ? (
          <div className="text-sm text-slate-500">No high-impact events in the next window.</div>
        ) : (
          <>
            <ul className="space-y-2">
              {nextEvents.map((e) => (
                <li key={e.id} className="rounded-lg border border-surface-2 bg-surface-1 px-3 py-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">{e.name}</div>
                      <div className="text-xs text-slate-400">
                        {e.country} · {new Date(e.datetime_utc).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                      </div>
                    </div>
                    <div className="rounded bg-red-500/10 px-2 py-0.5 text-[10px] font-medium uppercase text-red-400">{e.impact}</div>
                  </div>
                  <div className="mt-1 text-xs text-slate-500">so-what: watch {e.affected_assets.join(", ") || "related markets"} for volatility.</div>
                </li>
              ))}
            </ul>
            <div className="mt-2 text-[10px] text-slate-600">
              Source: {calendar?.source} · {calendar?.updated_at ? new Date(calendar.updated_at).toLocaleString() : ""} · delayed
            </div>
          </>
        )}
      </section>

      {hotTrackers.length > 0 && (
        <section>
          <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-400">Geopolitical hotspots</h3>
          <ul className="space-y-2">
            {hotTrackers.map((t) => (
              <li key={t.scope}>
                <Link
                  href={trackerHref(t.scope)}
                  className="block rounded-lg border border-surface-2 bg-surface-1 px-3 py-2 transition-colors hover:border-macro-accent/30"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{t.scope}</span>
                    <span className={`text-xs ${t.trend === "rising" ? "text-macro-down" : "text-slate-400"}`}>{t.trend}</span>
                  </div>
                  <div className="mt-1 text-xs text-slate-400">tension {t.value.toFixed(0)}/100</div>
                  <div className="mt-1 text-xs text-slate-500">
                    so-what: {t.trend === "rising" ? "safe-haven flows possible; risk tone fragile." : "stable but monitor headlines."}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
          <div className="mt-2 text-[10px] text-slate-600">
            Source: {geo?.source} · {geo?.updated_at ? new Date(geo.updated_at).toLocaleString() : ""} · delayed
          </div>
        </section>
      )}

      {extremes.length > 0 && (
        <section>
          <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-400">Positioning extremes</h3>
          <ul className="space-y-2">
            {extremes.map((p) => (
              <li key={p.instrument} className="rounded-lg border border-surface-2 bg-surface-1 px-3 py-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">{p.instrument}</span>
                  <span className="text-slate-400">{p.percentile.toFixed(0)}th %ile</span>
                </div>
                <div className="text-xs text-slate-500">{p.percentile > 80 ? "crowded long" : "crowded short"}</div>
                <div className="mt-1 text-xs text-slate-500">
                  so-what: extreme positioning can mean reversal risk if a catalyst hits.
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-2 text-[10px] text-slate-600">
            Source: {positioning?.source} · {positioning?.updated_at ? new Date(positioning.updated_at).toLocaleString() : ""} · delayed
          </div>
        </section>
      )}
    </div>
  );
}
